/**
  Networking interface
 */

import {
  DeviceType,
  CreateApplicationPayload,
  ApplicationWorkflowEvent,
} from '../constants';

import { query as graphqlQuery, CreditCardLegalDocument } from './graphql';

export const createCreditCardApplication = async (
  creditCardApplication: CreateApplicationPayload
) => {
  const createCreditCardApplicationMutation = `
    mutation createCreditCardApplication($creditCardApplication: CreditCardApplicationInput!) {
      createCreditCardApplication(creditCardApplication: $creditCardApplication) {
        applicationId
        hasConsentedToTcpa
        applicationStatus
        applicationAttribution {
          offerCode
        }
      }
    }
  `;

  const variables = {
    creditCardApplication,
  };

  return graphqlQuery(
    'createCreditCardApplication',
    createCreditCardApplicationMutation,
    variables
  );
};

export const getCreditCardApplicationLegalDocument = async (
  applicationId: string,
  legalDocumentName: string
) => {
  const getCreditCardApplicationLegalDocumentQuery = `
    query getCreditCardApplicationLegalDocument($applicationId: UUID!, $legalDocumentName: String!) {
      getCreditCardApplicationLegalDocument(applicationId: $applicationId, legalDocumentName: $legalDocumentName) {
        name
        signedViewUrl
        signedDownloadUrl
        version
      }
    }
  `;

  const variables = {
    applicationId,
    legalDocumentName,
  };

  return graphqlQuery(
    'getCreditCardApplicationLegalDocument',
    getCreditCardApplicationLegalDocumentQuery,
    variables
  );
};

export const getCreditCardApplicationsByUser = async (
  queryFields: Object,
  loginRedirect = true
) => {
  const getCreditCardApplicationsByUserQuery = `
    query getCreditCardApplicationsByUser($queryFields: CreditCardApplicationsByUserInput) {
      getCreditCardApplicationsByUser(queryFields: $queryFields) {
        data {
          applicationId
          applicationStatus
          externalUserId
        }
      }
    }
  `;

  const variables = {
    queryFields,
  };

  return graphqlQuery(
    'getCreditCardApplicationsByUser',
    getCreditCardApplicationsByUserQuery,
    variables,
    {},
    loginRedirect
  );
};

export const getCreditCardApplications = async (queryFields: Object) => {
  const getCreditCardApplicationsQuery = `
  query getCreditCardApplications($queryFields: CreditCardApplicationsQueryInput!) {
    getCreditCardApplications(queryFields: $queryFields) {
      data {
        applicationId
        applicationStatus
      }
    }
  }
  `;

  const variables = {
    queryFields,
  };

  return graphqlQuery(
    'getCreditCardApplications',
    getCreditCardApplicationsQuery,
    variables
  );
};

export const getLoggedInUser = async () => {
  const getLoggedInUserQuery = `
    query getLoggedInUser {
      getLoggedInUser {
        externalUserId
        firstName
        lastName
        dateOfBirth
        email
        ssn
        roles
      }
    }
  `;

  return graphqlQuery('getLoggedInUser', getLoggedInUserQuery);
};

// Get an application by appicationId
export const getCreditCardApplication = async (applicationId: string) => {
  const getCreditCardApplicationQuery = `
    query getCreditCardApplication($applicationId: UUID!) {
      getCreditCardApplication(applicationId: $applicationId) {
        applicationId
        applicationStatus
        externalUserId
        applicant {
          applicantId
          firstName
          lastName
        }
        offerCheck {
          offer {
            creditLimit
            conditionalOnCashflowVerification
            creditApr
          }
        }
        selectedBankAccount {
          maskedAccountNumber
          bankName
          plaidInstitutionId
        }
        autopayPreferences {
          paymentAmountType
          monthlyPaymentDay
        }
        plaidLinkToken
        applicationAttribution {
          campaignRefCode
          funnelType
        }
      }
    }
  `;

  const variables = {
    applicationId,
  };

  return graphqlQuery(
    'getCreditCardApplication',
    getCreditCardApplicationQuery,
    variables
  );
};

export const getCreditCardLegalDocuments = async (context: string) => {
  const getCreditCardLegalDocumentsQuery = `
    query getCreditCardLegalDocuments($context: String!) {
      getCreditCardLegalDocuments(context: $context) {
        name
        title
        version
        viewUrl
        downloadUrl
      }
    }
  `;

  const variables = {
    context,
  };

  return graphqlQuery(
    'getCreditCardLegalDocuments',
    getCreditCardLegalDocumentsQuery,
    variables
  );
};

export const getCreditCardApplicationDocuments = async (
  applicationId: string
) => {
  const getCreditCardApplicationDocumentsQuery = `
    query getCreditCardApplicationDocuments($applicationId: UUID!) {
      getCreditCardApplicationDocuments(applicationId: $applicationId) {
        documentId
        mimeType
        classification
        title
        createdDate
        signedDownloadUrl
        signedViewUrl
      }
    }
  `;

  const variables = {
    applicationId,
  };

  return graphqlQuery(
    'getCreditCardApplicationDocuments',
    getCreditCardApplicationDocumentsQuery,
    variables
  );
};

interface AddressPayload {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  stateOfResidence: string;
}

export interface UserPayload {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  password?: string;
  annualIncome: string;
  additionalIncome: string;
  tcpaConsent: boolean;
  eConsent: boolean;
  webBankPrivacyPolicyConsent: boolean;
  address: AddressPayload;
  employmentStatus: string;
}

export interface UpdateUserPayload {
  address: AddressPayload;
  phoneNumber?: string;
}

export const createUserMutation = async (user: UserPayload) => {
  const createUser = `
    mutation createUser($user: UserInput) {
      createUser(user: $user) {
        externalUserId
      }
    }
  `;

  const variables = {
    user,
  };

  return graphqlQuery('createUser', createUser, variables);
};

export const updateUserMutation = async (user: UpdateUserPayload) => {
  const updateUser = `
    mutation updateLoggedInUser($user: UpdateLoggedInUserInput) {
      updateLoggedInUser(user: $user) {
        user {
          phoneNumber
      }
    }
  }
`;
  const variables = {
    user,
  };
  return graphqlQuery('updateLoggedInUser', updateUser, variables);
};

// Current Implementation of processCreditCardApplicationWorkflowEvent:
// ------------------------------------------------------------------------------------------------

// On retrying GDS offer generation on OfferWaitingRoom page
// - Event:  OFFER_RETRIED
// - Status: OFFER_ERROR -> OFFER_IN_PROGRESS

// On submitting acceptance form on Offer page
// - Event:  OFFER_ACCEPTED
// - Status: PENDING_OFFER_ACCEPT -> PENDING_BANK_CONNECT_AUTHORIZATION

// On opening Plaid modal on ConnectToBank page
// - Event:  BANK_CONNECT_AUTHORIZED
// - Status: PENDING_BANK_CONNECT_AUTHORIZATION -> PENDING_BANK_CONNECT

// On completing Plaid onboaring on ConnectToBank page
// - Event:  BANK_CONNECTED
// - Status: PENDING_BANK_CONNECT -> VERIFICATION_IN_PROGRESS

// On retrying cash flow verification on VerificationWaitingRoom page
// - Event:  VERIFICATION_RETRIED
// - Status: VERIFICATION_ERROR -> VERIFICATION_IN_PROGRESS

// On selecting a statement preference on the Paperless page
// - Event:  APPLICATION_SUBMITTED
// - Status: PENDING_SUBMISSION -> EXPORT_IN_PROGRESS

// On retrying Fiserv export on ExportWaitingRoom page
// - Event:  EXPORT_RETRIED
// - Status: EXPORT_ERROR -> EXPORT_IN_PROGRESS

export interface ProcessCreditCardApplicationWorkflowEventAttributes {
  legalDocumentsToSave?: CreditCardLegalDocument[];
  plaidPublicToken?: string;
  plaidSelectedBankAccountId?: string;
  ssn?: string;
  onboardingOverride?: boolean;
}

export const processCreditCardApplicationWorkflowEvent = async (
  applicationId: string,
  applicationWorkflowEvent: ApplicationWorkflowEvent,
  applicationWorkflowEventAttributes?: ProcessCreditCardApplicationWorkflowEventAttributes
) => {
  const processCreditCardApplicationWorkflowEventMutation = `
    mutation processCreditCardApplicationWorkflowEvent($applicationId: UUID!, $event: CreditCardApplicationWorkflowEvent!, $attributes: CreditCardApplicationWorkflowEventAttributesInput) {
      processCreditCardApplicationWorkflowEvent(applicationId: $applicationId, event: $event, attributes: $attributes) {
        applicationId
        applicationStatus
        applicationWorkflowEvent
      }
    }
  `;

  const variables = {
    applicationId,
    event: applicationWorkflowEvent,
    attributes: applicationWorkflowEventAttributes,
  };

  return graphqlQuery(
    'processCreditCardApplicationWorkflowEvent',
    processCreditCardApplicationWorkflowEventMutation,
    variables
  );
};

// interface for createMarketingTouch
export interface MarketingTouchPayload {
  externalApplicationId: string | null;
  campaignRefCode: string;
  landingPageRefCode: string;
  persistentTouchTrackingId: string | null;
  sessionTouchTrackingId: string | null;
  marketingMetaDataJson: Record<string, any> | null;
  deviceType: DeviceType | null;
  offerCode: string | null;
}

export const createMarketingTouch = async (
  marketingTouch: MarketingTouchPayload
) => {
  const createMarketingTouchMutation = `
    mutation createMarketingTouch($marketingTouch: MarketingTouchInput!) {
      createMarketingTouch(marketingTouch: $marketingTouch) {
        externalMarketingTouchId
      }
    }
  `;

  const variables = {
    marketingTouch,
  };

  return graphqlQuery(
    'createMarketingTouch',
    createMarketingTouchMutation,
    variables,
    {},
    false,
    false
  );
};

// sends an offer code to get applicant info for a pre-approved offer
export const getMarketingOutbound = async (offerCode: string) => {
  const getMarketingOutboundQuery = `
    query getMarketingOutbound($offerCode: String!) {
      getMarketingOutbound(offerCode: $offerCode) {
        data {
          firstName
          lastName
          mailingAddressStreet
          mailingAddressCity
          mailingAddressState
          mailingAddressZipCode5
          campaignIsExpired
        }
      }
    }
  `;
  const variables = {
    offerCode,
  };
  return graphqlQuery(
    'getMarketingOutbound',
    getMarketingOutboundQuery,
    variables
  );
};

// get Plaid Link token
// redirectUri is used for bank connection with oAuth protocol
// link token is persisted to Application
export const createPlaidLinkToken = async (
  applicationId: string,
  redirectUri: string
) => {
  const createPlaidLinkTokenMutation = `
    mutation createCreditCardPlaidLinkToken($applicationId: UUID!, $plaidLinkTokenRequest: CreditCardPlaidLinkTokenInput!) {
      createCreditCardPlaidLinkToken(applicationId: $applicationId, plaidLinkTokenRequest: $plaidLinkTokenRequest) {
        plaidLinkToken
      }
    }
  `;

  const variables = {
    applicationId,
    plaidLinkTokenRequest: {
      redirectUri,
    },
  };

  return graphqlQuery(
    'createCreditCardPlaidLinkToken',
    createPlaidLinkTokenMutation,
    variables
  );
};

export interface AutoPayPreferencesPayload {
  legalDocumentsToSave?: CreditCardLegalDocument[];
  monthlyPaymentDay?: number;
  paymentAmountType: string;
}

export const createCreditCardApplicationAutopayPreferences = async (
  applicationId: string,
  autopayPreferences: AutoPayPreferencesPayload
) => {
  const createCreditCardApplicationAutopayPreferencesMutation = `
    mutation createCreditCardApplicationAutopayPreferences($applicationId: UUID!, $autopayPreferences: CreditCardApplicationAutopayPreferencesInput!) {
      createCreditCardApplicationAutopayPreferences(applicationId: $applicationId, autopayPreferences: $autopayPreferences) {
        paymentAmountType
        monthlyPaymentDay
      }
    }
  `;

  const variables = {
    applicationId,
    autopayPreferences,
  };

  return graphqlQuery(
    'createCreditCardApplicationAutopayPreferences',
    createCreditCardApplicationAutopayPreferencesMutation,
    variables
  );
};

export interface StatementPreferencesPayload {
  statementType: CreditCardApplicationStatementPreferencesStatementType;
}

export enum CreditCardApplicationStatementPreferencesStatementType {
  PAPER = 'PAPER',
  ELECTRONIC = 'ELECTRONIC',
}

export const createCreditCardApplicationStatementPreferences = async (
  applicationId: string,
  statementPreferences: StatementPreferencesPayload
) => {
  const createCreditCardApplicationStatementPreferencesMutation = `
    mutation createCreditCardApplicationStatementPreferences($applicationId: UUID!, $statementPreferences: CreditCardApplicationStatementPreferencesInput!) {
      createCreditCardApplicationStatementPreferences(applicationId: $applicationId, statementPreferences: $statementPreferences) {
        statementType
      }
    }
  `;

  const variables = {
    applicationId,
    statementPreferences,
  };

  return graphqlQuery(
    'createCreditCardApplicationStatementPreferences',
    createCreditCardApplicationStatementPreferencesMutation,
    variables
  );
};

export const getEmailExistenceQuery = async (email: string) => {
  const getEmailExistenceQueryString = `
    query getEmailExistence($email: String!){
      getEmailExistence(email: $email){
        exists
      }
    }
  `;

  const variables = {
    email,
  };

  return graphqlQuery(
    'getEmailExistence',
    getEmailExistenceQueryString,
    variables
  );
};

export const createDeviceValidationSession = async (
  applicationId: string,
  pageId: string
) => {
  const createDeviceValidationSessionMutation = `
      mutation createCreditCardDeviceValidationSessionByApplicationId($applicationId: UUID!, $pageId: String!) {
        createCreditCardDeviceValidationSessionByApplicationId(applicationId: $applicationId, pageId: $pageId) {
          deviceSessionId
          policyId
        }
    }
  `;

  const variables = {
    applicationId,
    pageId,
  };

  return graphqlQuery(
    'createCreditCardDeviceValidationSessionByApplicationId',
    createDeviceValidationSessionMutation,
    variables
  );
};

export const getCreditCardDeviceValidationSessionDetails = async (
  applicationId: string
) => {
  const getCreditCardDeviceValidationSessionDetailsQuery = `
    query getCreditCardDeviceValidationSessionDetails($applicationId: UUID!) {
      getCreditCardDeviceValidationSessionDetails(applicationId: $applicationId) {
        deviceSessionId
        isDeviceValidationSessionComplete
      }
    }
  `;

  const variables = {
    applicationId,
  };

  return graphqlQuery(
    'getCreditCardDeviceValidationSessionDetails',
    getCreditCardDeviceValidationSessionDetailsQuery,
    variables
  );
};

export const createExperimentTracking = async (
  experimentName: string,
  experimentVariation: string,
  eventName: string,
  externalUserId?: string,
  externalApplicationId?: string,
  vendorName?: string,
  remarks?: string
) => {
  const createExperimentTrackingMutation = `
    mutation createExperimentTracking($experimentName: String, $experimentVariation: String, $externalUserId: String, $externalApplicationId: String, $eventName: String, $vendorName: String, $remarks: String) {
      createExperimentTracking(
        experimentName: $experimentName,
        experimentVariation: $experimentVariation,
        externalUserId: $externalUserId,
        externalApplicationId: $externalApplicationId,
        eventName: $eventName,
        vendorName: $vendorName,
        remarks: $remarks
      ) {
        ExperimentTracking {
          externalExperimentEventId
        }
      }
    }
  `;

  const variables = {
    experimentName,
    experimentVariation,
    externalUserId,
    externalApplicationId,
    eventName,
    vendorName,
    remarks,
  };

  return graphqlQuery(
    'createExperimentTracking',
    createExperimentTrackingMutation,
    variables
  );
};

/** Income Collection operations */

// Validate the Income Collection Outbound record by ID (from url)
export const cardStarValidateCreditCardIncomeCollectionOutbound = async (
  incomeCollectionOutboundId: string
) => {
  const cardStarValidateCreditCardIncomeCollectionOutboundQuery = `
    query cardStarValidateCreditCardIncomeCollectionOutbound($incomeCollectionOutboundId: UUID!) {
      cardStarValidateCreditCardIncomeCollectionOutbound(incomeCollectionOutboundId: $incomeCollectionOutboundId) {
        incomeCollectionOutboundId
        isActive
        isIncomeUpdated
        latestReportedIncome
      }
    }
  `;

  const variables = {
    incomeCollectionOutboundId,
  };

  return graphqlQuery(
    'cardStarValidateCreditCardIncomeCollectionOutbound',
    cardStarValidateCreditCardIncomeCollectionOutboundQuery,
    variables,
    {},
    false,
    false
  );
};

interface CreditCardIncomeCollectionInput {
  annualIncome: string;
}

// Update the Income Collection record (annual income value) by ID
export const cardStarUpdateCreditCardIncomeCollection = async (
  incomeCollectionOutboundId: string,
  income: CreditCardIncomeCollectionInput
) => {
  const cardStarUpdateCreditCardIncomeCollectionMutation = `
    mutation cardStarUpdateCreditCardIncomeCollection($incomeCollectionOutboundId: UUID!, $income: CreditCardIncomeCollectionInput!) {
      cardStarUpdateCreditCardIncomeCollection(incomeCollectionOutboundId: $incomeCollectionOutboundId, income: $income) {
        incomeCollectionOutboundId
        annualIncome
      }
    }
  `;

  const variables = {
    incomeCollectionOutboundId,
    income,
  };

  return graphqlQuery(
    'cardStarUpdateCreditCardIncomeCollection',
    cardStarUpdateCreditCardIncomeCollectionMutation,
    variables,
    {},
    false,
    false
  );
};

export const coreDashboardGetCreditCardAccount = async (
  creditCardAccountId: string
) => {
  const coreDashboardGetCreditCardAccountQuery = `
    query coreDashboardGetCreditCardAccount($creditCardAccountId: String!) {
      coreDashboardGetCreditCardAccount(
        creditCardAccountId: $creditCardAccountId
      ) {
        accountMigrationStatus
      }
    }
  `;
  const variables = {
    creditCardAccountId,
  };
  return graphqlQuery(
    'coreDashboardGetCreditCardAccount',
    coreDashboardGetCreditCardAccountQuery,
    variables,
    {},
    true,
    true
  );
};

export const cardStarGetCreditCardAccountId = async () => {
  const cardStarGetCreditCardAccountIdQuery = `
    query cardStarGetCreditCardAccountId {
      cardStarGetCreditCardAccountId {
        creditCardAccountIds
      }
    }`;

  return graphqlQuery(
    'cardStarGetCreditCardAccountId',
    cardStarGetCreditCardAccountIdQuery,
    {},
    {},
    true,
    true
  );
};
